'use client'

import { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react'

import { ToastCard } from '@/components/molecules/toast-card.client'
import type { Toast, ToastInput } from '@/lib/toast'

/**
 * The app's one transient-message channel.
 *
 * Everything worth knowing about the design is in `lib/toast.ts` (how long a
 * message lives) and `toast-card.client.tsx` (how it announces itself). What
 * stays here is the part that has to be a provider: an id counter and a list.
 *
 * The live region below is rendered ALWAYS, empty or not, and that is not a
 * detail. A screen reader only announces changes INSIDE a region it was already
 * watching — a container mounted in the same tick as its first message is
 * frequently announced by nobody. Keeping the region on screen from first paint
 * is what makes every message after it audible.
 */

type ToastContextValue = {
  toast: (input: ToastInput) => void
}

const ToastContext = createContext<ToastContextValue | null>(null)

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([])
  const idRef = useRef(0)

  const toast = useCallback((input: ToastInput) => {
    idRef.current += 1
    setToasts((prev) => [...prev, { id: idRef.current, ...input }])
  }, [])

  const dismiss = useCallback((id: number) => {
    setToasts((prev) => prev.filter((item) => item.id !== id))
  }, [])

  const value = useMemo(() => ({ toast }), [toast])

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div
        // `polite` on the container and `alert` on an individual error card:
        // the container announces the ordinary ones without interrupting, and
        // an error carries its own assertive role so it does interrupt.
        aria-live="polite"
        aria-atomic="false"
        className="pointer-events-none fixed inset-x-0 bottom-4 z-[100] flex flex-col items-center gap-2 px-4 sm:bottom-6"
      >
        {toasts.map((item) => (
          <ToastCard key={item.id} toast={item} onDismiss={() => dismiss(item.id)} />
        ))}
      </div>
    </ToastContext.Provider>
  )
}

/**
 * The hook every caller uses. Falls back to a no-op outside the provider so a
 * component rendered in isolation (a story, a unit test) does not have to mount
 * the whole app to be rendered at all.
 */
export function useToast() {
  const ctx = useContext(ToastContext)
  return ctx ?? { toast: () => undefined }
}
